import { Event } from '@sentry/browser';
import { isPlainObject, map, mapValues } from 'lodash';

type SanitizedValue = '[Filtered]' | unknown;
type SanitizedRecord = Record<string, SanitizedValue>;
type SanitizedArray = (SanitizedRecord | unknown)[];
type Sanitized = SanitizedRecord | SanitizedArray | unknown;

const SENSITIVE_FIELDS = new Set([
  'ip_address',
  'email',
  'password',
  'confirmPassword',
]);

const sanitizeSensitiveFieldValue = (
  value: unknown,
  key: string
): SanitizedValue =>
  SENSITIVE_FIELDS.has(key) ? '[Filtered]' : sanitize(value);

const sanitize = (input: unknown): Sanitized => {
  if (Array.isArray(input)) return map<SanitizedArray>(input, sanitize);
  if (isPlainObject(input))
    return mapValues<SanitizedRecord>(
      input as Record<string, unknown>,
      sanitizeSensitiveFieldValue
    );
  return input;
};

const sanitizeSensitiveFields = <T>(incomingData: T): T => {
  const sanitized = sanitize(incomingData);
  return sanitized as T;
};

/**
 * Sanitizes data by identifying sensitive fields by their key and replacing their value with '[Filtered]'.
 * It only considers the fields of a Sentry Event where application/arbitrary data might appear,
 * such as `user`, `extra`, `request.data`, and `breadcrumbs.values`.
 *
 * § https://develop.sentry.dev/sdk/data-model/event-payloads/
 *
 * Note: This function mutates the passed event.
 */
export const sanitizeEvent = <T extends Event>(event: T): T => {
  if (!event) return event;
  if (event.user) {
    event.user = sanitizeSensitiveFields(event.user);
  }
  if (event.extra) {
    event.extra = sanitizeSensitiveFields(event.extra);
  }
  if (event.request?.data) {
    event.request.data = sanitizeSensitiveFields(event.request.data);
  }
  if (event.breadcrumbs?.values) {
    event.breadcrumbs.values = sanitizeSensitiveFields(
      event.breadcrumbs.values
    );
  }
  return event;
};
